import { useDroppable } from "@dnd-kit/core";
import { Heart, AlertTriangle } from "lucide-react";
import { CropChip } from "./CropChip";
import VeggieIcon from "@/components/VeggieIcon";

/**
 * A single cell in the bed grid.
 *
 * Drop highlights:
 *   - sage outline = valid empty target
 *   - terracotta outline = occupied target (will trigger swap dialog)
 *
 * Drag-time companion preview (when `isDragActive` is true and the cell is
 * empty):
 *   - dragHint === "antagonist" → red tint + ⚠ badge
 *   - dragHint === "companion"  → green tint + ❤ badge
 *
 * Companion sharing: a cell may host more than one crop when they're
 * mutual companions (validated server-side). We render the primary chip
 * normally and stack each companion's mini icon in the bottom-right
 * corner with a 🤝 affordance.
 */
export function GridCell({
    row, col, cropId, crop, companions = [], isAnchor,
    dragHint = "neutral", isDragActive = false,
    onEditCrop, onDeleteCrop,
}) {
    const isEmpty = !cropId;
    const sharedCompanions = companions.filter((c) => c?.id !== cropId);

    const { setNodeRef, isOver, active } = useDroppable({
        id: `cell-${row}-${col}`,
        data: { row, col, cropId, kind: "cell" },
    });

    const draggingSelfHere = active && active.data?.current?.cropId === cropId;
    const showOccupiedHover = isOver && !isEmpty && !draggingSelfHere;
    const showEmptyHover = isOver && isEmpty;

    const baseCls = "relative rounded-md border transition-colors min-h-[48px] flex items-center justify-center";
    const emptyCls = "bg-[var(--vm-input)]/40 border-[var(--vm-border)]";
    const spanCls = "bg-[var(--vm-input)]/70 border-[var(--vm-border)] border-dashed";
    const anchorCls = "bg-[var(--vm-card)] border-[var(--vm-border)]";
    const sharedCls = sharedCompanions.length
        ? "ring-1 ring-[var(--vm-sage)]/50"
        : "";

    // Companion / antagonist drag-time tint — only when the cell is empty
    // AND the user is mid-drag.
    let dragTintCls = "";
    if (isDragActive && isEmpty) {
        if (dragHint === "antagonist") {
            dragTintCls = "bg-[var(--vm-secondary)]/15 border-[var(--vm-secondary)]/60";
        } else if (dragHint === "companion") {
            dragTintCls = "bg-[var(--vm-sage)]/20 border-[var(--vm-sage)]/60";
        }
    }

    const hoverCls = showEmptyHover
        ? (dragHint === "antagonist"
            ? "ring-2 ring-[var(--vm-secondary)] ring-offset-1 ring-offset-[var(--vm-bg)] bg-[var(--vm-secondary)]/25"
            : dragHint === "companion"
                ? "ring-2 ring-[var(--vm-sage)] ring-offset-1 ring-offset-[var(--vm-bg)] bg-[var(--vm-sage)]/25"
                : "ring-2 ring-[var(--vm-sage)] ring-offset-1 ring-offset-[var(--vm-bg)] bg-[var(--vm-sage)]/15")
        : showOccupiedHover
            ? "ring-2 ring-[var(--vm-secondary)] ring-offset-1 ring-offset-[var(--vm-bg)]"
            : "";

    const showDragBadge = isDragActive && isEmpty && (dragHint === "antagonist" || dragHint === "companion");

    return (
        <div
            ref={setNodeRef}
            data-testid={`cell-${row}-${col}`}
            data-row={row}
            data-col={col}
            data-occupied={cropId ? "true" : "false"}
            data-companion-share={sharedCompanions.length || 0}
            data-drag-hint={dragHint}
            aria-label={
                isEmpty
                    ? `Row ${row}, column ${col}, empty${isDragActive && dragHint !== "neutral" ? `, ${dragHint} for the dragged crop` : ""}`
                    : `Row ${row}, column ${col}, ${crop?.crop_name || "occupied"}${sharedCompanions.length ? `, sharing slot with ${sharedCompanions.map((c) => c.crop_name).join(" + ")}` : ""}`
            }
            className={`${baseCls} ${isEmpty ? (dragTintCls || emptyCls) : isAnchor ? anchorCls : spanCls} ${sharedCls} ${hoverCls}`}
        >
            {isEmpty ? (
                <span className="text-[var(--vm-text-muted)] text-xs font-mono-grid opacity-50" aria-hidden>·</span>
            ) : isAnchor && crop ? (
                <div className="absolute inset-1">
                    <CropChip crop={crop} variant="cell" draggable onEdit={onEditCrop} onDelete={onDeleteCrop} />
                </div>
            ) : null}

            {/* Companion stack — small icons + 🤝 affordance, sits over the chip */}
            {sharedCompanions.length > 0 && isAnchor && (
                <span
                    data-testid={`companion-stack-${row}-${col}`}
                    title={`Companion slot — sharing with ${sharedCompanions.map((c) => c.crop_name).join(" + ")}`}
                    className="absolute -bottom-1 -right-1 z-10 flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-[var(--vm-sage)]/95 border border-[var(--vm-bg)] shadow-sm"
                >
                    {sharedCompanions.slice(0, 2).map((sc) => (
                        <span key={sc.id} className="block w-3.5 h-3.5 rounded-sm overflow-hidden bg-[var(--vm-bg)]">
                            <VeggieIcon cropName={sc.crop_name} stage={sc.stage} size={14} />
                        </span>
                    ))}
                    <Heart className="w-2.5 h-2.5 text-[var(--vm-bg)]" fill="currentColor" strokeWidth={0} />
                </span>
            )}

            {showDragBadge && (
                <span
                    aria-hidden
                    data-testid={`drag-hint-${row}-${col}`}
                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center shadow ${
                        dragHint === "antagonist"
                            ? "bg-[var(--vm-secondary)] text-[var(--vm-bg)]"
                            : "bg-[var(--vm-sage)] text-[var(--vm-bg)]"
                    }`}
                >
                    {dragHint === "antagonist"
                        ? <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.5} />
                        : <Heart className="w-2.5 h-2.5" strokeWidth={2.5} fill="currentColor" />}
                </span>
            )}
        </div>
    );
}
